import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { ActivationCodeService } from '../services/activationCodeService';
import { BookService } from '../../book/services/bookService';
import { BookCollectionService } from '../../book-collection/services/bookCollectionService';
import toast from 'react-hot-toast';

const CreateBatchDialog = ({ open, onOpenChange, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [books, setBooks] = useState([]);
  const [collections, setCollections] = useState([]);
  const [targetType, setTargetType] = useState('book'); // 'book' or 'collection'
  
  // Calculate default expiration date (5 years from now)
  const getDefaultExpiryDate = () => {
    const date = new Date();
    date.setFullYear(date.getFullYear() + 5);
    return date.toISOString().split('T')[0];
  };
  
  const [formData, setFormData] = useState({
    name: '',
    book_id: '',
    collection_id: '',
    total_codes: 50,
    expires_at: getDefaultExpiryDate(),
    description: '',
    code_prefix: '',
    max_download_limit: ''
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch books
        const booksResponse = await BookService.getBooks();
        setBooks(Array.isArray(booksResponse.data) ? booksResponse.data : []);
        
        // Fetch collections
        const collectionsResponse = await BookCollectionService.getCollections();
        setCollections(Array.isArray(collectionsResponse.data) ? collectionsResponse.data : []);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    
    if (open) {
      fetchData();
    }
  }, [open]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleTypeChange = (type) => {
    setTargetType(type);
    // Clear the other field when switching
    setFormData(prev => ({
      ...prev,
      book_id: type === 'book' ? prev.book_id : '',
      collection_id: type === 'collection' ? prev.collection_id : ''
    }));
    // Clear related errors
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors.book_id;
      delete newErrors.collection_id;
      return newErrors;
    });
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Tên lô mã là bắt buộc';
    }
    
    if (targetType === 'book' && !formData.book_id) {
      newErrors.book_id = 'Sách là bắt buộc';
    }
    
    if (targetType === 'collection' && !formData.collection_id) {
      newErrors.collection_id = 'Bộ sách là bắt buộc';
    }

    if (formData.code_prefix && formData.code_prefix.length > 10) {
      newErrors.code_prefix = 'Tiền tố không được vượt quá 10 ký tự';
    }

    if (!formData.total_codes || formData.total_codes < 1) {
      newErrors.total_codes = 'Số lượng phải lớn hơn 0';
    }

    if (formData.total_codes > 10000) {
      newErrors.total_codes = 'Số lượng không được vượt quá 10,000';
    }

    if (!formData.expires_at) {
      newErrors.expires_at = 'Ngày hết hạn là bắt buộc';
    } else {
      const expiryDate = new Date(formData.expires_at);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      if (expiryDate <= today) {
        newErrors.expires_at = 'Ngày hết hạn phải sau ngày hôm nay';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    try {
      let batchData = {
        name: formData.name,
        total_codes: formData.total_codes,
        expires_at: formData.expires_at,
        code_prefix: formData.code_prefix,
        sku: formData.code_prefix || 'DEFAULT', // Generate SKU from prefix only
        // Send only when the admin actually typed a value; '' means "use backend default"
        max_download_limit: formData.max_download_limit === '' ? undefined : formData.max_download_limit
      };

      if (targetType === 'book') {
        const selectedBook = books.find(book => book.id === formData.book_id);
        batchData.book_id = formData.book_id;
        batchData.book_title = selectedBook?.title || '';
      } else {
        const selectedCollection = collections.find(col => 
          (col.collection_id || col._id) === formData.collection_id
        );
        batchData.collection_id = formData.collection_id;
        batchData.collection_title = selectedCollection?.title || '';
      }

      // Call the real API
      const response = await ActivationCodeService.createBatch(batchData);
      
      if (response.success) {
        
        onSuccess?.();
        onOpenChange(false);
        
        // Reset form
        setFormData({
          name: '',
          book_id: '',
          collection_id: '',
          total_codes: 50,
          expires_at: getDefaultExpiryDate(),
          description: '',
          code_prefix: '',
          max_download_limit: ''
        });
        setTargetType('book');
        setErrors({});
      } else {
        throw new Error(response.message || 'Có lỗi xảy ra khi tạo lô mã');
      }
      
    } catch (error) {
      console.error('Error creating batch:', error);
      
      // Handle specific API errors
      if (error.response?.data?.message) {
        toast.error(`Lỗi: ${error.response.data.message}`);
      } else if (error.message) {
        toast.error(`Lỗi: ${error.message}`);
      } else {
        toast.error('Có lỗi xảy ra khi tạo lô mã');
      }
    } finally {
      setLoading(false);
    }
  };

  const selectedBook = books.find(book => book.id === formData.book_id);
  const selectedCollection = collections.find(col => 
    (col.collection_id || col._id) === formData.collection_id
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Tạo lô mã kích hoạt mới</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Tên lô mã */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tên lô mã <span className="text-red-500">*</span>
            </label>
            <Input
              placeholder="VD: Lô mã sách Toán học"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              className={`placeholder:text-gray-400 ${errors.name ? 'border-red-500' : ''}`}
            />
            {errors.name && (
              <p className="text-red-500 text-sm mt-1">{errors.name}</p>
            )}
          </div>

          {/* Loại mục tiêu */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Loại mục tiêu <span className="text-red-500">*</span>
            </label>
            <div className="flex space-x-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="targetType"
                  value="book"
                  checked={targetType === 'book'}
                  onChange={() => handleTypeChange('book')}
                  className="mr-2"
                />
                <span className="text-sm">Sách đơn lẻ</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="targetType"
                  value="collection"
                  checked={targetType === 'collection'}
                  onChange={() => handleTypeChange('collection')}
                  className="mr-2"
                />
                <span className="text-sm">Bộ sách</span>
              </label>
            </div>
          </div>

          {/* Sách (hiện khi chọn "book") */}
          {targetType === 'book' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Sách <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.book_id}
                onChange={(e) => handleChange('book_id', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder:text-gray-400 ${
                  errors.book_id ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="" className="text-gray-400">Chọn sách</option>
                {books.map(book => (
                  <option key={book.id} value={book.id}>{book.title}</option>
                ))}
              </select>
              {errors.book_id && (
                <p className="text-red-500 text-sm mt-1">{errors.book_id}</p>
              )}
            </div>
          )}

          {/* Bộ sách (hiện khi chọn "collection") */}
          {targetType === 'collection' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Bộ sách <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.collection_id}
                onChange={(e) => handleChange('collection_id', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder:text-gray-400 ${
                  errors.collection_id ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="" className="text-gray-400">Chọn bộ sách</option>
                {collections.map(collection => (
                  <option 
                    key={collection._id || collection.collection_id} 
                    value={collection.collection_id || collection._id}
                  >
                    {collection.title} ({collection.book_count || collection.book_ids?.length || 0} sách)
                  </option>
                ))}
              </select>
              {errors.collection_id && (
                <p className="text-red-500 text-sm mt-1">{errors.collection_id}</p>
              )}
              <p className="text-xs text-gray-500 mt-1">
                Mã sẽ áp dụng cho tất cả các sách trong bộ sách
              </p>
            </div>
          )}

          {/* Tiền tố mã */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tiền tố mã
            </label>
            <Input
              placeholder="VD: BOOK (tùy chọn)"
              value={formData.code_prefix}
              onChange={(e) => handleChange('code_prefix', e.target.value.toUpperCase())}
              className={`placeholder:text-gray-400 ${errors.code_prefix ? 'border-red-500' : ''}`}
              maxLength={10}
            />
            {errors.code_prefix && (
              <p className="text-red-500 text-sm mt-1">{errors.code_prefix}</p>
            )}
            <p className="text-xs text-gray-500 mt-1">Tối đa 10 ký tự (tùy chọn)</p>
          </div>

          {/* Row với Số lượng và Hạn sử dụng */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Số lượng mã <span className="text-red-500">*</span>
              </label>
              <Input
                type="number"
                min="1"
                max="10000"
                placeholder="50"
                value={formData.total_codes}
                onChange={(e) => handleChange('total_codes', parseInt(e.target.value) || 0)}
                className={`placeholder:text-gray-400 ${errors.total_codes ? 'border-red-500' : ''}`}
              />
              {errors.total_codes && (
                <p className="text-red-500 text-sm mt-1">{errors.total_codes}</p>
              )}
              <p className="text-xs text-gray-500 mt-1">Tối đa 10,000 mã mỗi lô</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Ngày hết hạn <span className="text-red-500">*</span>
              </label>
              <Input
                type="date"
                value={formData.expires_at}
                onChange={(e) => handleChange('expires_at', e.target.value)}
                className={`placeholder:text-gray-400 ${errors.expires_at ? 'border-red-500' : ''}`}
                min={new Date().toISOString().split('T')[0]}
              />
              {errors.expires_at && (
                <p className="text-red-500 text-sm mt-1">{errors.expires_at}</p>
              )}
            </div>
          </div>

          {/* Max Download Limit */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Số thiết bị tối đa
            </label>
            <Input
              type="number"
              min="1"
              placeholder="Để trống để sử dụng mặc định (2)"
              value={formData.max_download_limit}
              onChange={(e) => handleChange('max_download_limit', e.target.value ? parseInt(e.target.value) : '')}
              className="placeholder:text-gray-400"
            />
            <p className="text-xs text-gray-500 mt-1">Mặc định: 2 thiết bị (để trống để sử dụng mặc định)</p>
          </div>

          {/* Mô tả */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Mô tả
            </label>
            <Textarea
              placeholder="Mô tả về mục đích sử dụng lô mã này..."
              rows={3}
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              className="placeholder:text-gray-400"
            />
          </div>

          {/* Preview info */}
          {((targetType === 'book' && selectedBook) || (targetType === 'collection' && selectedCollection)) && (
            <div className={`${targetType === 'collection' ? 'bg-purple-50' : 'bg-blue-50'} p-4 rounded-lg`}>
              <h4 className={`font-medium ${targetType === 'collection' ? 'text-purple-900' : 'text-blue-900'} mb-2`}>
                📋 Thông tin lô mã sẽ tạo:
              </h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className={targetType === 'collection' ? 'text-purple-700' : 'text-blue-700'}>
                    {targetType === 'collection' ? 'Bộ sách:' : 'Sách:'}
                  </span>{' '}
                  {targetType === 'collection' ? selectedCollection?.title : selectedBook?.title}
                </div>
                <div>
                  <span className={targetType === 'collection' ? 'text-purple-700' : 'text-blue-700'}>
                    Số lượng:
                  </span>{' '}
                  {formData.total_codes} mã
                </div>
                {targetType === 'collection' && selectedCollection && (
                  <div>
                    <span className="text-purple-700">Số sách:</span>{' '}
                    {selectedCollection.book_count || selectedCollection.book_ids?.length || 0} sách
                  </div>
                )}
                <div>
                  <span className={targetType === 'collection' ? 'text-purple-700' : 'text-blue-700'}>
                    Tên lô:
                  </span>{' '}
                  {formData.name || 'Chưa nhập'}
                </div>
                <div className="col-span-2">
                  <span className={targetType === 'collection' ? 'text-purple-700' : 'text-blue-700'}>
                    Format:
                  </span>{' '}
                  {formData.code_prefix ? `${formData.code_prefix}-XXXXXXXXX` : 'XXXXXXXXX'}
                </div>
              </div>
            </div>
          )}

          {/* Action buttons */}
          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              disabled={loading}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Hủy
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <span className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Đang tạo...
                </span>
              ) : (
                '🚀 Tạo lô mã'
              )}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateBatchDialog;
